import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button,Skeleton,Stack,TextField } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useMutation,useQuery } from '@tanstack/react-query';
import MUIDataTable,{ MUIDataTableOptions } from 'mui-datatables';
import React,{ useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiDeleteBin5Line } from "react-icons/ri";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Clients from "../../service/Clients";
import queryClient from '../../service/query';
import { getMuiTheme,tableOptions } from "../TableHelpers/options";

const customerService = new Clients();

export default function AddCustomerTag() {
  const [tagName, setTagName] = useState<string>('');
  const { t } = useTranslation('translation');


  const { data, isLoading, isFetching, error, isSuccess } = useQuery({
    queryKey: ['GET_TAGS'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token) {
          const response: { tags: { id: string, name: string }[] } = await customerService.getClientTag(token);
          if (!response || !response.tags) throw new Error('No tags found');
          if (response) {
            return response.tags;
          }
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    retry: 0
  });

  useEffect(() => {
    if (isSuccess) trackEventMatomo('tags de clientes', 'sucesso', 'carregamento', 'tags carregadas');
    if (error) trackEventMatomo('tags de clientes', 'erro', 'carregamento', 'falha ao carregar tags');
  }, [error, isSuccess]);

  const { mutate: addNewTag, isPending: isAddingTag } = useMutation({
    mutationKey: ['ADD_NEW_TAG'],
    mutationFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        const promise = toast.promise(async () => {
          const body = { ...token, tag: tagName };
          const response: { allTags: { [key: string]: string } } = await customerService.createClientTag(body);
          if (!response || !response.allTags) throw new Error('No tag created');
        }, {
          pending: t('toast.saving'),
          success: t('customerPage.tagCreated'),
          error: t('toast.errorCreatingNewTag'),
        }, {
          toastId: 'createTag',
        });
        return promise;
      } catch (err) {
        console.error(err);
      }
    },
    onSuccess: () => {
      trackEventMatomo('tags de clientes', 'sucesso', 'criação', 'tag criada');
      queryClient.invalidateQueries({ queryKey: ['GET_TAGS'] });
      setTagName('');
    },
    onError: () => trackEventMatomo('tags de clientes', 'erro', 'criação', 'falha ao criar tag'),
  });

  const { mutate: removeTag, isPending: isRemovingTag } = useMutation({
    mutationKey: ['REMOVE_TAG'],
    mutationFn: async (value: string) => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        const body = { ...token, tagId: value };
        const response = await customerService.deleteClientTag(body);
        if (!response || !response.success) throw new Error('Tag not deleted');
        return response;
      } catch (err) {
        console.error(err);
      }
    },
    onSuccess: () => {
      trackEventMatomo('tags de clientes', 'sucesso', 'exclusão', 'tag removida');
      queryClient.invalidateQueries({ queryKey: ['GET_TAGS'] });
    },
    onError: () => trackEventMatomo('tags de clientes', 'erro', 'exclusão', 'falha ao remover tag'),
  });

  const onLoading = isLoading || isFetching || isAddingTag || isRemovingTag;

  const columns = [
    {
      name: 'name',
      label: t('tag'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'id',
      label: t('delete'),
      options: {
        customBodyRender: (value: string) => (
          <Button
            endIcon={<RiDeleteBin5Line />}
            type='button'
            className="button is-danger"
            onClick={() => {
              trackEventMatomo('Adicionar tag cliente', 'click', 'button', 'deletar tag');
              removeTag(value);
              toast.error(t('customerPage.tagExcluded'), { toastId: 'customerPage.tagExcluded' });
            }}
          >

            {t('delete')}
          </Button>
        ),
        sort: false,
        filter: false
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  if (error) {
    return <span>{t('notifications.unread.errorLoading')}</span>;
  }

  return (
    <Stack
      width={'100%'}
      direction="column"
      spacing={1}
    >
      <Stack
        width={'100%'}
        component='form'
        onSubmit={(e) => {
          e.preventDefault();
          addNewTag();
          trackEventMatomo('Adicionar tag cliente', 'click', 'button', 'criar nova tag');
        }}
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={2}
      >
        <TextField
          fullWidth
          value={tagName}
          disabled={onLoading}
          onClick={() => trackEventMatomo('Adicionar tag cliente', 'click', 'input', 'editar valor tag')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setTagName(e.target.value);
          }}
          label={t('users.tagControll.inputPlacholder')}
        />
        <Button
          endIcon={<AddCircleIcon />}
          type="submit"
          color='success'
          variant='contained'
          disabled={tagName === '' || onLoading}
        >
          {`${t('register')}`}
        </Button>
      </Stack>
      {onLoading
        ? <Skeleton variant='rounded' width={'100%'} height={400} />
        : <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title={t('users.tagControll.tagList')}
            columns={columns}
            data={data || []}
            options={{ ...tableOptions, ...translatedTextLabels }}
          />
        </ThemeProvider>}

    </Stack>
  );
}
